import { useCallback, useEffect } from "react";
import { useParams } from "react-router";

import {
  CustomerPaymentMethodsQuery,
  gqlCustomerPaymentMethods,
  CustomerInitiatePaymentMutation,
  gqlCustomerInitiatePayment,
  CustomerQueryPaymentMutation,
  gqlCustomerQueryPayment,
  CustomerCancelPaymentMutation,
  gqlCustomerCancelPayment,
  CustomerInitialPaymentInput,
  CustomerPaymentInitiateResponse,
} from "../generated/graphql";
import { isRequestLoaded } from "../models/RequestState";

import { useGraphqlQuery, useGraphqlMutation } from "./graphql";

export function useCustomerPaymentMethodsQuery() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const { refetch, requestState } = useGraphqlQuery<
    CustomerPaymentMethodsQuery
  >(gqlCustomerPaymentMethods, {
    variables: { slug: companySlug },
    fetchPolicy: "cache-and-network",
  });
  const callRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);
  // Refetch when back from other screens
  useEffect(() => {
    if (isRequestLoaded(requestState)) {
      refetch().then(
        () => {},
        () => {}
      );
    }
  }, []); //eslint-disable-line react-hooks/exhaustive-deps
  return { requestState, refetch: callRefetch };
}

export function useCustomerInitiatePayment(): (
  input: CustomerInitialPaymentInput
) => Promise<CustomerPaymentInitiateResponse> {
  const { companySlug } = useParams<{ companySlug: string }>();
  const mutation = useGraphqlMutation<CustomerInitiatePaymentMutation>(
    gqlCustomerInitiatePayment
  );
  return useCallback(
    async (input: CustomerInitialPaymentInput) => {
      const res = await mutation({
        variables: {
          slug: companySlug,
          input,
        },
      });
      if (!res) {
        throw new Error();
      }
      return res.CustomerInitiatePayment;
    },
    [companySlug, mutation]
  );
}

export function useCustomerQueryPayment() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const mutation = useGraphqlMutation<CustomerQueryPaymentMutation>(
    gqlCustomerQueryPayment
  );
  return useCallback(
    async (paymentId: string) => {
      const res = await mutation({
        variables: {
          slug: companySlug,
          id: paymentId,
        },
      });
      if (!res) {
        throw new Error();
      }
      return res.CustomerQueryPayment;
    },
    [companySlug, mutation]
  );
}

export function useCustomerCancelPayment() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const mutation = useGraphqlMutation<CustomerCancelPaymentMutation>(
    gqlCustomerCancelPayment
  );
  return useCallback(
    async (paymentId: string) => {
      const res = await mutation({
        variables: {
          slug: companySlug,
          id: paymentId,
        },
      });
      if (!res) {
        throw new Error();
      }
      return res.CustomerCancelPayment;
    },
    [companySlug, mutation]
  );
}
